// Imports nothing, so node:test can run it without the Sentry SDK.

/**
 * A network breadcrumb keeps only where the request went, never what it
 * carried in its address. An upload's own address holds the class, the
 * person's uid and the FILE NAME, and a resumable Storage or R2 address is a
 * live key to keep sending into that upload. Every upload failure now sends
 * a Sentry event (reportUploadFailureToSentry in sentry.ts), so those addresses
 * would have ridden along on exactly those events. The Hub's own function
 * calls keep their path (/.netlify/functions/attachmentUpload), which names
 * no one; any other host keeps only its origin. Query strings never stay.
 * This covers breadcrumbs only: performance spans (1 in 10 page loads) still
 * carry the full address, which is older than this and is a follow-up.
 */
export function scrubRequestBreadcrumb<T extends { category?: string; data?: Record<string, unknown> }>(
  breadcrumb: T,
): T {
  if ((breadcrumb.category !== 'xhr' && breadcrumb.category !== 'fetch') || !breadcrumb.data) return breadcrumb;
  const url = breadcrumb.data.url;
  if (typeof url !== 'string') return breadcrumb;
  let kept = '[address removed]';
  try {
    const here = typeof window !== 'undefined' ? window.location.origin : 'https://hub.invalid';
    const parsed = new URL(url, here);
    kept = parsed.origin === here ? parsed.pathname : parsed.origin;
  } catch {
    // An address that does not parse is dropped whole.
  }
  return { ...breadcrumb, data: { ...breadcrumb.data, url: kept } };
}

/**
 * THE SHARE TOKEN NEVER LEAVES IN A REPORT (N14 refute round 1, H1). A comp
 * share page's address, /s/<token>, is a family's secret. Sentry does not
 * start on that page at all (sentry.ts, main.tsx, shareMain.tsx); this is
 * the second lock, for anything that reports an address anyway: an event's
 * request.url, a transaction's name, a span's description, a navigation
 * breadcrumb's from and to. Every /s/<anything> becomes /s/:token.
 */
//
// ANCHORED to the start of a path (refute round 2, L-1): /s/ must open the
// string, follow whitespace, a quote, a bracket, a comma, '=' (plain or as
// %3D), or come straight after an origin (scheme://host, plain or as
// scheme%3A%2F%2Fhost). So https://cdn.example/x/s/player.js keeps its
// address. The separators may be percent-encoded (%2Fs%2F<token>, a
// redirect's ?next=), and the encoding is kept as it was.
const SHARE_PATH_RE = /(^|[\s"'(\[,=`|:;]|%3[Dd]|[a-zA-Z][a-zA-Z0-9+.-]*:\/\/[^/\s"'?#]+|[a-zA-Z][a-zA-Z0-9+.-]*%3[Aa]%2[Ff]%2[Ff][^/\s"'?#%]+)(\/|%2[Ff])s(\/|%2[Ff])[^/?#\s"'<>&%]+/g;

//
// THE TOKEN-SHAPED NET (refute round 3): the anchored rule above misses a
// share path after any other lead (path:/s/<t>, x|/s/<t>, a bare host with
// no scheme like hub.thestudioldg.com/s/<t>) and a double-encoded one
// (%252Fs%252F<t>). So, ANYWHERE in a string, /s/ (plain, %2F or %252F)
// followed by a run of 16 or more token characters (base64url; a real token
// is 43) is scrubbed too. A Hub path never has a 16-character id straight
// after a lone /s/, and https://cdn.example/x/s/player.js stops at its dot.
const SEP = '(\\/|%2[Ff]|%252[Ff])';
const SHARE_TOKEN_SHAPED_RE = new RegExp(`${SEP}s${SEP}[A-Za-z0-9_-]{16,}`, 'g');

export function scrubShareTokens(text: string): string {
  return text
    .replace(SHARE_PATH_RE, (_match, lead: string, a: string, b: string) => `${lead}${a}s${b}:token`)
    .replace(SHARE_TOKEN_SHAPED_RE, (_match, a: string, b: string) => `${a}s${b}:token`);
}

/** Every string anywhere in a Sentry event or transaction, scrubbed. */
export function scrubShareTokensDeep<T>(value: T, depth = 0): T {
  if (typeof value === 'string') return scrubShareTokens(value) as T;
  if (depth > 12 || value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map((item) => scrubShareTokensDeep(item, depth + 1)) as T;
  const proto = Object.getPrototypeOf(value);
  if (proto !== Object.prototype && proto !== null) return value;
  const out: Record<string, unknown> = {};
  for (const [key, inner] of Object.entries(value as Record<string, unknown>)) {
    out[key] = scrubShareTokensDeep(inner, depth + 1);
  }
  return out as T;
}

/** The breadcrumb hook: request addresses trimmed, share tokens scrubbed. */
export function scrubBreadcrumb<T extends { category?: string; data?: Record<string, unknown>; message?: string }>(
  breadcrumb: T,
): T {
  return scrubShareTokensDeep(scrubRequestBreadcrumb(breadcrumb));
}
